import type { RootState } from '@/redux/store'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'

import { getParticipatedFormCovers } from '@/lib/api/participatedFormApi'
import { BasedOnOrder, GetCoversQuery, GetParticipatedFormCoversQuery } from '@/typings/domain'

type OnlyFilledForm<T> = T extends { form: unknown } ? T : never
export type ParticipatedFormType = OnlyFilledForm<
  NonNullable<NonNullable<NonNullable<GetParticipatedFormCoversQuery['formCovers']>['nodes']>[number]>
>

type GetParticipatedFormsResult = Omit<NonNullable<GetCoversQuery['formCovers']>, 'nodes'> & {
  nodes: ParticipatedFormType[]
}

const participatedFormCoversAdapter = createEntityAdapter<ParticipatedFormType, string>({
  selectId: model => model.form.token,
})

const participatedFormCoverSlice = createSlice({
  name: 'participatedForms',
  initialState: participatedFormCoversAdapter.getInitialState({
    isLoading: false,
    loaded: false,
    pageInfo: {
      totalCount: 0,
      hasNextPage: false,
      endCursor: '',
    },
  }),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchParticipatedFormCoversThunk.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchParticipatedFormCoversThunk.fulfilled, (state, action) => {
        if (action.payload.reset) {
          participatedFormCoversAdapter.setAll(state, action.payload.nodes)
        } else {
          participatedFormCoversAdapter.upsertMany(state, action.payload.nodes)
        }
        state.pageInfo.totalCount = action.payload.totalCount
        state.pageInfo.hasNextPage = action.payload.pageInfo.hasNextPage
        state.pageInfo.endCursor = action.payload.pageInfo.endCursor ?? ''
        state.isLoading = false
        state.loaded = true
      })
  },
})

interface FetchParticipatedFormsPayload {
  reset?: boolean
}

export const fetchParticipatedFormCoversThunk = createAsyncThunk<
  GetParticipatedFormsResult & { reset?: boolean },
  FetchParticipatedFormsPayload,
  { state: RootState }
>('participated_forms/fetchParticipatedFormCovers', async (payload, thunkAPI) => {
  const state = thunkAPI.getState().participatedFormCovers
  const { reset } = payload
  const params = {
    sort: BasedOnOrder.Manual,
    first: 20,
    after: reset ? null : state.pageInfo.endCursor,
  }

  const { data } = await getParticipatedFormCovers(params)
  if (!data?.formCovers) {
    return thunkAPI.rejectWithValue('failed to load participated forms')
  }

  return { ...(data.formCovers as GetParticipatedFormsResult), reset }
})

export const participatedFormCoversSelectors = participatedFormCoversAdapter.getSelectors<RootState>(
  state => state.participatedFormCovers
)

export default participatedFormCoverSlice.reducer
